import { captureEvent } from "@sentry/react";
import {
  type BudgetovacPositionFragment,
  BudgetovacPositionsDocument,
  type BudgetovacPositionsQuery,
  type BudgetovacPositionsQueryVariables,
  DeleteBudgetovacPositionDocument,
  type DeleteBudgetovacPositionMutation,
  type DeleteBudgetovacPositionMutationVariables,
} from "@src/__generated__/graphql";
import { BudgetPositionCreateStore } from "@src/components/modules/budgets/positions/stores/create";
import { BudgetPositionUpdateStore } from "@src/components/modules/budgets/positions/stores/update";
import type { BodyRow } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { action, computed, makeObservable, observable } from "mobx";

export interface BudgetovacPositionTableRow
  extends BodyRow,
    BudgetovacPositionFragment {
  departmentName?: string;
}

export class BudgetPositionsStore {
  readonly tableKey = "budget-positions-table";

  createStore: BudgetPositionCreateStore;
  updateStore: BudgetPositionUpdateStore;

  deleteModalDisclosure = new DisclosureState<{ id: string }>();

  private positionsFetcher = new FetchHelper<
    BudgetovacPositionsQuery,
    BudgetovacPositionsQueryVariables
  >(BudgetovacPositionsDocument);

  private deleteMutator = new MutationHelper<
    DeleteBudgetovacPositionMutation,
    DeleteBudgetovacPositionMutationVariables
  >(DeleteBudgetovacPositionDocument);

  @observable.ref
  positions: BudgetovacPositionTableRow[] = [];

  constructor(private appStore: AppStore) {
    this.createStore = new BudgetPositionCreateStore();
    this.updateStore = new BudgetPositionUpdateStore(appStore);
    makeObservable(this);
    this.fetchData();
  }

  @computed
  get isLoadingPositions(): boolean {
    return this.positionsFetcher.isLoading.value;
  }

  @computed
  get isDeleting(): boolean {
    return this.deleteMutator.isLoading.value;
  }

  @action.bound
  async fetchData(): Promise<void> {
    const [data, error] = await this.positionsFetcher.fetch();

    if (error) return;

    this.positions = data.budgetovacPositions.map((position) => ({
      id: position.id,
      name: position.name,
      department_id: position.department_id,
      departmentName: position.department_id
        ? this.createStore.departmentOptions.find(
            (option) => option.value === String(position.department_id),
          )?.label
        : undefined,
    }));
  }

  @computed
  get getPositionToDelete(): BudgetovacPositionFragment | undefined {
    return this.positions.find(
      (position) =>
        position.id === this.deleteModalDisclosure.additionalData?.id,
    );
  }

  @action.bound
  async deletePosition(): Promise<void> {
    const positionId = this.deleteModalDisclosure.additionalData?.id;

    if (!positionId) {
      captureEvent({
        message: "FE: Missing position ID while trying to delete position.",
      });
      this.appStore.UIStore.toast({ status: "error" });
      return;
    }

    const [, error] = await this.deleteMutator.mutate({ id: positionId });

    if (error) return;

    this.fetchData();
  }

  @action.bound
  async createPosition(): Promise<void> {
    const success = await this.createStore.createPosition();

    if (!success) return;

    this.fetchData();
  }

  @action.bound
  async updatePosition(): Promise<void> {
    const success = await this.updateStore.updatePosition();

    if (!success) return;

    this.fetchData();
  }
}
